import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Fingerprint,
  LogOut,
  Pencil,
  ScrollText,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import { userBalances } from "@/lib/balances";
import {
  competitionRanks,
  formatCompetitionRank,
  sharedRankFlags,
} from "@/lib/competitionRank";
import { formatBRL } from "@/lib/format";
import {
  computeLeaderboards,
  type LeaderboardEntry,
  type LeaderboardSet,
  type Member,
  type SettlementSummary,
} from "@/lib/leaderboard";
import { selectFeatured } from "@/lib/tournaments";
import {
  biometricsServerEnabled,
  biometricsSupported,
  disableBiometrics,
  enableBiometrics,
  isWebauthnCancel,
} from "@/lib/webauthn";
import { useToast } from "@/components/ui/Toast";
import {
  MyBetsList,
  type LiveLeaderboardEntry,
} from "@/components/MyBetsList";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import type { Tournament } from "@/types";

type MyBet = {
  id: string;
  tournamentId: string;
  playerIds: string[];
  playerNames: string[];
  amount: number;
  createdAt: string;
};

// Rank do usuário em um leaderboard ordenado (mesma semântica da página de
// Rankings): posição de competição (1224) e 0 quando o valor é zero — quem
// nunca pontuou não tem colocação, mostra "—".
function myRankIn(
  list: readonly LeaderboardEntry[],
  userId: string,
  tieKey: (e: LeaderboardEntry) => number,
): { rank: number; tied: boolean } {
  const idx = list.findIndex((u) => u.userId === userId);
  if (idx < 0 || tieKey(list[idx]!) === 0) return { rank: 0, tied: false };
  const ranks = competitionRanks(list, tieKey);
  return { rank: ranks[idx]!, tied: sharedRankFlags(ranks)[idx]! };
}

// Rótulo de seção fora do card — mesmo estilo do sectionLabel original
// (11px bold, letterspacing 1.5, maiúsculas).
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-[11px] font-bold tracking-[1.5px] text-muted-foreground uppercase">
      {children}
    </p>
  );
}

// Réplica do MenuItem original: ícone em tile secundário, título + subtítulo
// e chevron à direita.
function MenuItem({
  icon,
  title,
  subtitle,
  to,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const content = (
    <>
      <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-(--radius-field) bg-secondary">
        {icon}
      </div>
      <div className="min-w-0 flex-1 text-left">
        <p className="text-sm font-semibold">{title}</p>
        <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
      </div>
      {!disabled && (
        <ChevronRight size={18} className="shrink-0 text-muted-foreground" />
      )}
    </>
  );
  const className = `flex w-full items-center gap-3 rounded-(--radius-btn) border border-border bg-card p-3.5 transition-colors ${
    disabled ? "opacity-50" : "hover:border-tint/40"
  }`;
  if (disabled) {
    return <div className={className}>{content}</div>;
  }
  if (to) {
    return (
      <Link to={to} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  );
}

export default function PerfilPage() {
  const { user, logout, refreshUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editNickname, setEditNickname] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Torneio em destaque (mesma seleção da Home) — contextualiza o texto de
  // saldos e o título da seção "Meu palpite".
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [myBets, setMyBets] = useState<MyBet[]>([]);
  const [boards, setBoards] = useState<LeaderboardSet | null>(null);
  const [settlements, setSettlements] = useState<SettlementSummary[]>([]);
  useEffect(() => {
    apiFetch<Tournament[]>("/api/tournaments", { auth: false })
      .then(setTournaments)
      .catch(() => {});
    apiFetch<{ bets: MyBet[] }>("/api/me/bets")
      .then((res) => setMyBets(res.bets))
      .catch(() => {});
    Promise.all([
      apiFetch<{ members: Member[] }>("/api/members"),
      apiFetch<{ settlements: SettlementSummary[] }>("/api/settlements"),
    ])
      .then(([{ members }, { settlements: s }]) => {
        setSettlements(s);
        setBoards(computeLeaderboards(members, s));
      })
      .catch(() => {});
  }, []);
  const featured = useMemo(() => selectFeatured(tournaments), [tournaments]);

  // Placar ao vivo do torneio em destaque (só quando ativo, como o
  // original), com poll de 60s — alimenta posição/score/CUT do MyBetsList.
  const [liveLeaderboard, setLiveLeaderboard] = useState<
    LiveLeaderboardEntry[]
  >([]);
  const featuredActiveId =
    featured?.status === "active" ? featured.id : undefined;
  useEffect(() => {
    if (!featuredActiveId) return;
    let cancelled = false;
    const load = () => {
      apiFetch<LiveLeaderboardEntry[]>(
        `/api/tournaments/${encodeURIComponent(featuredActiveId)}/leaderboard`,
        { auth: false },
      )
        .then((rows) => {
          if (!cancelled) setLiveLeaderboard(rows);
        })
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [featuredActiveId]);
  // Top-3 ao vivo resolvido (mesma regra do app original: pertencimento
  // exato em vez de position <= 3, que super-destaca empates).
  const officialTop3Ids = useMemo(() => {
    const ids = liveLeaderboard
      .filter((e) => e.position !== null)
      .slice(0, 3)
      .map((e) => e.playerId);
    return ids.length === 3 ? ids : undefined;
  }, [liveLeaderboard]);

  // Exclusão de palpite (deadline aberto): DELETE com refund server-side.
  const [deletingBetId, setDeletingBetId] = useState<string | null>(null);
  const deleteBet = async (betId: string) => {
    setDeletingBetId(betId);
    try {
      const res = await apiFetch<{ refunded: number }>(`/api/bets/${betId}`, {
        method: "DELETE",
      });
      setMyBets((prev) => prev.filter((b) => b.id !== betId));
      await refreshUser();
      showToast({
        type: "success",
        message: `Palpite excluído. ${formatBRL(res.refunded)} devolvidos ao saldo.`,
      });
    } catch (err) {
      showToast({
        type: "error",
        message:
          err instanceof Error ? err.message : "Não foi possível excluir.",
      });
    } finally {
      setDeletingBetId(null);
    }
  };

  // Biometria (WebAuthn): suporte do aparelho + estado da conta no servidor.
  const [bioSupported, setBioSupported] = useState(false);
  const [bioEnabled, setBioEnabled] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  useEffect(() => {
    void biometricsSupported().then((ok) => {
      setBioSupported(ok);
      if (ok) {
        biometricsServerEnabled()
          .then(setBioEnabled)
          .catch(() => {});
      }
    });
  }, []);

  const [pixKey, setPixKey] = useState(user?.pixKey ?? "");
  const [savingPix, setSavingPix] = useState(false);
  const [amountStr, setAmountStr] = useState("");
  const [requesting, setRequesting] = useState(false);

  // Saldo Total = disponível + créditos + em palpites (apostas ainda sem
  // resultado) — mesma composição do card original.
  const balances = useMemo(
    () =>
      user
        ? userBalances(user, myBets, tournaments)
        : { available: 0, credit: 0, inPlay: 0, total: 0 },
    [user, myBets, tournaments],
  );

  const rankHits = useMemo(
    () =>
      boards && user
        ? myRankIn(boards.byHits, user.id, (e) => e.hits)
        : { rank: 0, tied: false },
    [boards, user],
  );
  const rankEarnings = useMemo(
    () =>
      boards && user
        ? myRankIn(boards.byEarnings, user.id, (e) => e.totalWon)
        : { rank: 0, tied: false },
    [boards, user],
  );

  if (!user) return null;

  const openEdit = () => {
    setEditName(user.name);
    setEditNickname(user.nickname ?? "");
    setEditEmail(user.email);
    setEditing(true);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await apiFetch("/api/auth/me", {
        method: "PATCH",
        body: JSON.stringify({
          name: editName.trim(),
          nickname: editNickname.trim(),
          email: editEmail.trim(),
        }),
      });
      await refreshUser();
      setEditing(false);
      showToast({ type: "success", message: "Perfil atualizado." });
    } catch (err) {
      showToast({
        type: "error",
        message:
          err instanceof Error ? err.message : "Não foi possível salvar.",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePix = async () => {
    setSavingPix(true);
    try {
      await apiFetch("/api/me/pix-key", {
        method: "PUT",
        body: JSON.stringify({ pixKey: pixKey.trim() }),
      });
      await refreshUser();
      showToast({ type: "success", message: "Chave PIX salva." });
    } catch (err) {
      showToast({
        type: "error",
        message: err instanceof Error ? err.message : "Erro ao salvar.",
      });
    } finally {
      setSavingPix(false);
    }
  };

  const toggleBiometrics = async () => {
    if (!bioSupported || bioBusy) return;
    if (bioEnabled) {
      if (
        !window.confirm(
          "Desativar o login por biometria? As credenciais registradas serão removidas da sua conta.",
        )
      ) {
        return;
      }
      setBioBusy(true);
      try {
        await disableBiometrics();
        setBioEnabled(false);
        showToast({ type: "success", message: "Biometria desativada." });
      } catch (err) {
        showToast({
          type: "error",
          message:
            err instanceof Error ? err.message : "Não foi possível desativar.",
        });
      } finally {
        setBioBusy(false);
      }
      return;
    }
    setBioBusy(true);
    try {
      await enableBiometrics();
      setBioEnabled(true);
      showToast({
        type: "success",
        message: "Biometria ativada! Use-a no próximo login.",
      });
    } catch (err) {
      if (!isWebauthnCancel(err)) {
        showToast({
          type: "error",
          message:
            err instanceof Error ? err.message : "Não foi possível ativar.",
        });
      }
    } finally {
      setBioBusy(false);
    }
  };

  const amount = Number.parseInt(amountStr, 10);
  const amountValid =
    Number.isFinite(amount) && amount > 0 && amount <= balances.available;

  const requestWithdrawal = async () => {
    if (!amountValid || requesting) return;
    setRequesting(true);
    try {
      await apiFetch("/api/me/withdraw", {
        method: "POST",
        body: JSON.stringify({ amount }),
      });
      await refreshUser();
      setAmountStr("");
      showToast({
        type: "success",
        message:
          "Saque solicitado! O pagamento será feito via PIX em até 2 dias úteis.",
      });
    } catch (err) {
      showToast({
        type: "error",
        message:
          err instanceof Error ? err.message : "Não foi possível solicitar.",
      });
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 px-5 pt-6 pb-4">
      {/* Header — réplica do original: marca PERFIL, avatar com inicial,
          nome 22 bold + linha muted, botão redondo de edição. */}
      <header>
        <p className="text-[11px] font-bold tracking-[2px] text-muted-foreground">
          PERFIL
        </p>
        <div className="mt-3 flex items-center gap-3.5">
          <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full bg-primary">
            <span className="text-[22px] font-bold text-primary-foreground">
              {user.name.charAt(0)}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[22px] leading-tight font-bold">
              {user.name}
            </p>
            <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
              {user.nickname?.trim() ? user.nickname : user.email}
            </p>
          </div>
          <button
            type="button"
            onClick={openEdit}
            aria-label="Editar perfil"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary transition-opacity hover:opacity-70"
          >
            <Pencil size={16} className="text-foreground" />
          </button>
        </div>
      </header>

      {editing && (
        <div
          className="fixed inset-0 z-20 flex items-end justify-center bg-black/60 sm:items-center"
          onClick={() => !savingProfile && setEditing(false)}
        >
          <Card
            className="w-full max-w-lg rounded-b-none p-5 sm:rounded-(--radius-app)"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold">Editar perfil</h2>
            <div className="mt-4 flex flex-col gap-3">
              <Field
                label="Nome"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Seu nome"
              />
              <Field
                label="Apelido (exibido nos rankings)"
                value={editNickname}
                onChange={(e) => setEditNickname(e.target.value)}
                placeholder="Seu apelido"
              />
              <Field
                label="E-mail"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                inputMode="email"
                placeholder="seu@email.com"
              />
            </div>
            <div className="mt-5 flex gap-2">
              <Button
                variant="secondary"
                title="Cancelar"
                disabled={savingProfile}
                onClick={() => setEditing(false)}
                className="flex-1"
              />
              <Button
                title="Salvar"
                loading={savingProfile}
                disabled={editName.trim().length < 2 || !editEmail.includes("@")}
                onClick={() => void saveProfile()}
                className="flex-1"
              />
            </div>
          </Card>
        </div>
      )}

      {/* Saldo — card SALDO TOTAL em primary + breakdown em 3 colunas,
          como no original (sem CTA de depósito: créditos vêm do admin). */}
      <section>
        <div className="rounded-(--radius-app) bg-primary p-[18px]">
          <p className="text-[10px] font-medium tracking-[1px] text-primary-foreground/80">
            SALDO TOTAL
          </p>
          <p
            className="mt-1 text-[26px] leading-tight font-bold text-primary-foreground"
            data-testid="perfil-total"
          >
            {formatBRL(balances.total)}
          </p>
        </div>
        <div className="mt-2 flex gap-2 rounded-(--radius-field) border border-border bg-card px-3.5 py-2.5">
          <div className="flex-1">
            <p className="text-[9px] font-medium tracking-[0.6px] text-muted-foreground">
              DISPONÍVEL
            </p>
            <p className="text-[13px] font-bold" data-testid="perfil-available">
              {formatBRL(balances.available)}
            </p>
          </div>
          <div className="flex-1">
            <p className="text-[9px] font-medium tracking-[0.6px] text-muted-foreground">
              CRÉDITOS
            </p>
            <p className="text-[13px] font-bold" data-testid="perfil-credits">
              {formatBRL(balances.credit)}
            </p>
          </div>
          <div className="flex-1">
            <p className="text-[9px] font-medium tracking-[0.6px] text-muted-foreground">
              EM PALPITES
            </p>
            <p className="text-[13px] font-bold" data-testid="perfil-in-play">
              {formatBRL(balances.inPlay)}
            </p>
          </div>
        </div>
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          {featured ? `Para o torneio ${featured.name}, você` : "Você"} só
          poderá se utilizar dos créditos que a plataforma te disponibilizou
          para fazer seus palpites, mas se você acertar, seu prêmio será em
          dinheiro.
        </p>
      </section>

      {/* Sua posição — card surface-alt com ranks dourados, como o original. */}
      <section>
        <SectionLabel>Sua posição</SectionLabel>
        <div className="flex items-center rounded-(--radius-app) bg-surface-alt p-[18px]">
          <div className="flex flex-1 flex-col items-center gap-1.5">
            <p className="text-[10px] font-medium tracking-[1px] text-surface-alt-foreground/60">
              POR ACERTOS
            </p>
            <p className="text-[28px] leading-none font-bold text-accent">
              {rankHits.rank > 0
                ? formatCompetitionRank(rankHits.rank, rankHits.tied)
                : "—"}
            </p>
          </div>
          <div className="h-9 w-px bg-surface-alt-foreground/10" />
          <div className="flex flex-1 flex-col items-center gap-1.5">
            <p className="text-[10px] font-medium tracking-[1px] text-surface-alt-foreground/60">
              POR GANHOS
            </p>
            <p className="text-[28px] leading-none font-bold text-accent">
              {rankEarnings.rank > 0
                ? formatCompetitionRank(rankEarnings.rank, rankEarnings.tied)
                : "—"}
            </p>
          </div>
        </div>
      </section>

      {/* Meu palpite — mesmo componente/layout do app original
          (components/MyBetsList.tsx): card por aposta com badge de
          resultado, posição/score ao vivo, CUT e exclusão com refund. */}
      <section>
        <SectionLabel>
          {featured ? `Meu palpite — ${featured.name}` : "Meu palpite"}
        </SectionLabel>
        <MyBetsList
          bets={myBets}
          tournaments={tournaments}
          settlements={settlements}
          limit={5}
          onSeeAll={() => navigate("/meus-palpites")}
          liveLeaderboard={liveLeaderboard}
          liveLeaderboardTournamentId={featuredActiveId}
          officialTop3Ids={officialTop3Ids}
          onDelete={(betId) => void deleteBet(betId)}
          deletingBetId={deletingBetId}
        />
      </section>

      <section>
        <SectionLabel>
          Chave PIX (necessário para recebimento dos prêmios)
        </SectionLabel>
        <Card className="p-5">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field
                label="CPF, e-mail, telefone ou chave aleatória"
                value={pixKey}
                onChange={(e) => setPixKey(e.target.value)}
                placeholder="Sua chave PIX"
              />
            </div>
            <Button
              title="Salvar"
              variant="secondary"
              loading={savingPix}
              disabled={pixKey.trim() === (user.pixKey ?? "")}
              onClick={() => void savePix()}
              className="h-[46px] px-4"
            />
          </div>
        </Card>
      </section>

      <section>
        <SectionLabel>Solicitar saque</SectionLabel>
        <Card className="p-5">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field
                label={`Disponível: ${formatBRL(balances.available)}`}
                value={amountStr}
                onChange={(e) =>
                  setAmountStr(e.target.value.replace(/[^0-9]/g, ""))
                }
                inputMode="numeric"
                placeholder="Valor (R$)"
              />
            </div>
            <Button
              title="Solicitar"
              loading={requesting}
              disabled={!amountValid || !user.pixKey}
              onClick={() => void requestWithdrawal()}
              className="h-[46px] px-4"
            />
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {!user.pixKey
              ? "Cadastre sua chave PIX acima para solicitar saques."
              : "O valor é debitado na hora e o pagamento é feito via PIX pela organização."}
          </p>
        </Card>
      </section>

      {/* Configurações — menu no formato do original (biometria não se
          aplica ao web). */}
      <section>
        <SectionLabel>Configurações</SectionLabel>
        <div className="flex flex-col gap-2.5">
          <MenuItem
            icon={<ScrollText size={18} className="text-tint" />}
            title="Extrato"
            subtitle="Histórico de depósitos, saques, palpites e prêmios"
            to="/extrato"
          />
          <MenuItem
            icon={<Fingerprint size={18} className="text-tint" />}
            title="Entrar com biometria"
            subtitle={
              !bioSupported
                ? "Não disponível neste aparelho"
                : bioBusy
                  ? "Aguarde..."
                  : bioEnabled
                    ? "Ativado — toque para desativar"
                    : "Desativado — toque para ativar"
            }
            disabled={!bioSupported}
            onClick={() => void toggleBiometrics()}
          />
          <MenuItem
            icon={<LogOut size={18} className="text-foreground" />}
            title="Sair"
            subtitle="Encerrar sessão neste dispositivo"
            onClick={logout}
          />
        </div>
      </section>

      <p className="text-center text-[11px] text-muted-foreground">
        Bolão PGA · v1.0
      </p>
    </div>
  );
}
